import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { Router, NavigationEnd, ActivatedRoute } from "@angular/router";
import { Location } from "@angular/common";
import { filter } from "rxjs/operators";

export interface HeaderConfig {
  showBackButton: boolean;
  showSearch: boolean;
  showActivities: boolean;
  showQuickActions: boolean;
  pageTitle?: string;
}

@Injectable({
  providedIn: "root",
})
export class HeaderService {
  private readonly headerConfigSubject = new BehaviorSubject<HeaderConfig>({
    showBackButton: false,
    showSearch: true,
    showActivities: true,
    showQuickActions: true,
    pageTitle: "",
  });

  public headerConfig$: Observable<HeaderConfig> =
    this.headerConfigSubject.asObservable();

  constructor(
    private readonly router: Router,
    private readonly location: Location,
    private readonly activatedRoute: ActivatedRoute
  ) {
    // Automatically configure header based on route data
    this.initRouteListener();
  }

  /**
   * Listen to route changes and configure header based on route data
   */
  private initRouteListener() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const routeData = this.getRouteData(this.activatedRoute);

        if (routeData?.["header"]) {
          const headerConfig = routeData["header"];

          if (headerConfig.type === "detail") {
            this.showDetailPageHeader(headerConfig.title || "");
          } else if (headerConfig.type === "dashboard" || headerConfig.type === "main") {
            this.showDashboardHeader();
          } else {
            // Custom configuration
            this.updateHeaderConfig(headerConfig);
          }
        } else {
          // If route doesn't have header config, reset to dashboard header
          this.showDashboardHeader();
        }
      });
  }

  /**
   * Get route data from the deepest child route
   */
  private getRouteData(route: ActivatedRoute): any {
    let child = route.firstChild;
    while (child) {
      if (child.firstChild) {
        child = child.firstChild;
      } else if (child.snapshot.data) {
        return child.snapshot.data;
      } else {
        return null;
      }
    }
    return route.snapshot.data;
  }

  /**
   * Update header configuration
   */
  updateHeaderConfig(config: Partial<HeaderConfig>) {
    const currentConfig = this.headerConfigSubject.value;
    this.headerConfigSubject.next({ ...currentConfig, ...config });
  }

  /**
   * Show detail page header (with back button, no search)
   */
  showDetailPageHeader(pageTitle: string) {
    this.updateHeaderConfig({
      showBackButton: true,
      showSearch: false,
      showActivities: false,
      showQuickActions: false,
      pageTitle,
    });
  }

  /**
   * Show normal dashboard header (with search, no back button)
   */
  showDashboardHeader() {
    this.updateHeaderConfig({
      showBackButton: false,
      showSearch: true,
      showActivities: true,
      showQuickActions: true,
      pageTitle: "",
    });
  }

  /**
   * Navigate back
   */
  goBack() {
    const currentUrl = this.router.url;

    // Check if we're on the add-new-contract page (add, edit, or view)
    if (
      currentUrl.includes("/admin-center/contract-template/add-template") ||
      currentUrl.includes("/admin-center/contract-template/edit/")
    ) {
      this.router.navigate(["/admin-center/contract-template-list"]);
    } else if (currentUrl.includes("/admin-center/save-po-template")) {
      this.router.navigate(["/admin-center/po-builder"]);
    } else if (currentUrl.includes("/master/department/deleted")) {
      // Navigate back to department page from deleted departments
      this.router.navigate(["/master/department"]);
    } else if (currentUrl.includes("/master/category/deleted")) {
      // Navigate back to category page from deleted categories
      this.router.navigate(["/master/category"]);
    } else if (currentUrl.includes("/master/attribute/deleted")) {
      // Navigate back to attribute page from deleted attributes
      this.router.navigate(["/master/attribute"]);
    } else if (currentUrl.includes("/master/uom/deleted")) {
      // Navigate back to UOM page from deleted UOMs
      this.router.navigate(["/master/uom"]);
    } else if (currentUrl.includes('/master/manage-users/deleted')) {
        // Navigate back to manage users page from deleted users
        this.router.navigate(['/master/manage-users']);
    } else if (
      currentUrl.includes("/system-preference-master/new-workflow") ||
      currentUrl.includes("/system-preference-master/edit-workflow/:id")
    ) {
      // Navigate back to approval hierarchy page
      this.router.navigate(["/system-preference-master/system-preference"], {
        queryParams: { tab: "Approval Hierarchy" },
      });
    } else if (
      currentUrl.includes("/system-preference-master/manage-permissions")
    ) {
      // Navigate back to manage permissions page
      this.router.navigate(["/system-preference-master/system-preference"], {
        queryParams: { tab: "Manage Roles" },
      });
    } else {
      // Default back navigation
      this.location.back();
    }
  }

  /**
   * Get current header config
   */
  getCurrentConfig(): HeaderConfig {
    return this.headerConfigSubject.value;
  }
}
